import { useEffect } from 'react';

import { mean } from 'lodash-es';

import type { HeroFeedItem, PsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { SOCIAL_STARTER_ROUTE_ID } from '#src/app/pages/psf/promoteOrShare/shared/consts';
import { useGuestInfo, usePageCopy } from '#src/app/pages/psf/promoteOrShare/shared/hooks';
import { useCurrentRouteConfig } from '#src/app/routes';
import { useLoginState } from '#src/app/shared/hooks/session';
import { useTrackOnce } from '#src/app/shared/hooks/tracking';

type Result = ModelHookResult<{
	heading: string;
	subHeading1: string;
	subHeading2: string;
	showImpactBox: boolean;
	heroFeed: {
		items: readonly HeroFeedItem[];
		show: boolean;
	};
	pageTitle: string;
	// No displayable cover image: PageBody drops <PetitionMedia /> entirely.
	hideMediaSlot: boolean;
	// Only ever true on the Social Starter route.
	socialStarterShareFirst: boolean;
	/**
	 * Full resolved Share First Social Starter experiment, so PageBody can fire exposure for every
	 * arm (including control, where the card itself never mounts). Only set on the Social Starter
	 * route — on combo this is undefined, so PageBody never mounts the exposure tracker there.
	 */
	psfShareFirstSocialStarterExperiment?: PsfPromoteOrSharePageData['psfShareFirstSocialStarterExperiment'];
	// EXPERIMENT: psf_og_text_experiment — remove with experiment cleanup
	psfOgTextExperiment: PsfPromoteOrSharePageData['psfOgTextExperiment'];
	/**
	 * Petition id, for `resetKeys` on exposure tracking — re-fires per petition on SPA nav that
	 * reuses this component without remounting.
	 */
	petitionId: string;
}>;

const MIN_FEED_ITEMS = 2;

export function usePageBody(): Result {
	const {
		data: { headlineText, headlineTextIdentified, subheadingText1, subheadingText2, progressBarLabelText },
	} = usePageCopy();
	const loginState = useLoginState();
	const hasGuestInfo = !!useGuestInfo();
	const isSocialStarterRoute = useCurrentRouteConfig()?.config.id === SOCIAL_STARTER_ROUTE_ID;

	const pageTitle = progressBarLabelText;
	const {
		data: {
			petition: {
				id: petitionId,
				promotionStats,
				feeds: { heroFeed },
			},
			hideMediaSlot,
			psfShareFirstSocialStarterExperiment,
			psfOgTextExperiment,
		},
	} = usePsfPromoteOrSharePageData();
	const trackOnce = useTrackOnce();

	const showFeed = heroFeed.length >= MIN_FEED_ITEMS;

	useEffect(() => {
		const promotionAmounts = heroFeed
			.map((item) => item.event)
			.filter((event) => event.__typename === 'HeroFeedPromotionEvent')
			.map((event) => event.amountUsd);
		void trackOnce('combo_feed_treated', {
			combo_feed_displayed: showFeed,
			...(showFeed
				? {
						combo_feed_item_count: heroFeed.length,
						combo_feed_photo_count: heroFeed.filter((item) => !!item.userPhoto).length,
						combo_feed_promotion_count: heroFeed.filter((item) => item.event.__typename === 'HeroFeedPromotionEvent')
							.length,
						combo_feed_share_count: heroFeed.filter((item) => item.event.__typename === 'HeroFeedShareEvent').length,
						combo_feed_amounts_usd: promotionAmounts,
						combo_feed_amounts_usd_mean: mean(promotionAmounts),
						combo_feed_shares_percent: 0,
					}
				: undefined),
		});
	}, [trackOnce]);

	const pageHeading = loginState === 'GUEST' && !hasGuestInfo ? headlineText : headlineTextIdentified;

	return {
		data: {
			heading: pageHeading,
			subHeading1: subheadingText1,
			subHeading2: subheadingText2,
			showImpactBox: promotionStats.signatureCount > 1,
			heroFeed: {
				items: heroFeed,
				show: showFeed,
			},
			pageTitle,
			hideMediaSlot,
			socialStarterShareFirst: isSocialStarterRoute && psfShareFirstSocialStarterExperiment.variation !== 'control',
			psfShareFirstSocialStarterExperiment: isSocialStarterRoute ? psfShareFirstSocialStarterExperiment : undefined,
			psfOgTextExperiment,
			petitionId,
		},
	};
}
